/* eslint-disable react/display-name */
import Typography from "@material-ui/core/Typography";
import React, { FC } from "react";
import { Button } from "@material-ui/core";
import { Cancel, CheckCircle, CloudDownload } from "@material-ui/icons";
import { useDispatch, useSelector } from "react-redux";

import BaseDataTable from "../../../../../components/modules/BaseDataTable";
import Loader from "../../../../../components/elements/Loader";
import Switch from "../../../../../components/elements/Switch";
import classes from "../../SelectHubspotTargetCompaniesSlide.module.scss";
import csvDownload from "../../../../../utils/csvExport";
import { FoundCompanyInfo, FoundPrimaryCompanyInfo } from "../../../../../types/campaignTypes";
import { ReduxApplicationState } from "../../../../../types/ReduxTypes/ReduxApplicationState";
import { setPrimarySegmentsInclude } from "../../../../../redux/reducers/campaignToCreate";
import { statusColors } from "../../../../../theme/brandgenColorPalette";

type CompaniesDashboardProps = {
  tierValue: string[];
  areTargetAccountsOnly: boolean;
  areCompaniesLoading: boolean;
  primarySegments: FoundPrimaryCompanyInfo[] | undefined;
  uniqueSegments: FoundCompanyInfo[] | undefined;
};

const CompaniesDashboard: FC<CompaniesDashboardProps> = ({ areCompaniesLoading, primarySegments, uniqueSegments }) => {
  const columns = [
    { key: "domain", label: "Domain", minWidth: 170 },
    { key: "include", label: "Include", minWidth: 170 },
    {
      key: "matched",
      label: "Matched",
      minWidth: 120,
    },
  ] as const;

  const dispatch = useDispatch();
  const fromFile = useSelector((state: ReduxApplicationState) => state.campaignToCreate.fromFile);

  const handleLineItemActiveStatusChange = (domain: string, include: boolean) => () => {
    dispatch(setPrimarySegmentsInclude(domain, !include));
  };

  const primaryRows = primarySegments?.map((primarySegment, index) => ({
    id: `${primarySegment.domain}-${index}`,
    domain: primarySegment.domain,
    matched: primarySegment.domainInfo ? (
      <CheckCircle htmlColor={statusColors.success} />
    ) : (
      <Cancel htmlColor={statusColors.error} />
    ),
    include: (
      <Switch
        checked={primarySegment.include}
        onChange={handleLineItemActiveStatusChange(primarySegment.domain, primarySegment.include)}
      />
    ),
  }));

  const uniqueRows = uniqueSegments?.map((uniqueSegment, index) => ({
    id: `${uniqueSegment.domain}-${index}`,
    domain: uniqueSegment.domain,
    matched: uniqueSegment.domainInfo ? (
      <CheckCircle htmlColor={statusColors.success} />
    ) : (
      <Cancel htmlColor={statusColors.error} />
    ),
    include: null,
  }));

  const rows = [...(primaryRows || []), ...(uniqueRows || [])];

  const totalMatches =
    (primarySegments || []).filter((segment) => segment.domainInfo).length +
    (uniqueSegments || []).filter((segment) => segment.domainInfo).length;

  return (
    <div className={classes.companiesDashboardContainer}>
      {areCompaniesLoading ? (
        <div className={classes.loaderContainer}>
          <Typography>We're getting your company information. This may take a few minutes...</Typography>
          <Loader isAbsolutePosition={false} size={42} />
        </div>
      ) : null}

      {primarySegments && uniqueSegments && !areCompaniesLoading ? (
        <div className={classes.dashboardContainer}>
          <Typography className={classes.matchedCompaniesInfo}>
            We were able to match{" "}
            <strong>
              {totalMatches} out of {primarySegments.length + uniqueSegments.length}
            </strong>{" "}
            companies.
            {!fromFile &&
              " The domains that we use for data collection are pulled from the 'domain' property associated with the company in HubSpot. If the domain listed below appears incorrect, update this property in HubSpot and then pull the list again."}
          </Typography>
          <Button
            style={{
              marginBottom: "20px",
              float: "right",
            }}
            variant="contained"
            startIcon={<CloudDownload />}
            onClick={() => {
              csvDownload({
                data: rows.map((row) => ({
                  Domain: row.domain,
                  Matched: row.matched.props.htmlColor === statusColors.success ? "Yes" : "No",
                })),
                filename: "export.csv",
                delimiter: ",",
              });
            }}
          >
            Export
          </Button>

          {primaryRows && primaryRows.length > 0 && (
            <Typography className={classes.subdomainsWarning}>
              Currently, our targeting features do not extend into targeting subdomains (example:
              subdomain.primarydomain.com). Selecting to include reference to a subdomains in the targeting inclusion
              list will result in targeting the primary domain. (example: subdomain.primarydomain.com).
            </Typography>
          )}

          <BaseDataTable columns={columns} rows={rows} />
        </div>
      ) : null}
    </div>
  );
};

export default CompaniesDashboard;
